import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Loader2, Plus, Trash2, Edit2, Save, FileText, X, ClipboardList,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ROLES = ['practice_admin', 'physician', 'nurse', 'care_coordinator', 'aide', 'billing_specialist', 'director_patient_services', 'service_provider', 'asset_manager'];

export default function ServiceRequestTypesPage() {
  const [types, setTypes] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, fRes] = await Promise.all([
        axios.get(`${API_URL}/api/service-request-types?active_only=false`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/forms`, { headers: authHeader() }).catch(() => ({ data: { forms: [] } })),
      ]);
      setTypes(tRes.data?.types || []);
      setForms(fRes.data?.forms || fRes.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name?.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const payload = {
        name: editing.name,
        description: editing.description || null,
        linked_form_id: editing.linked_form_id || null,
        assignee_role: editing.assignee_role || null,
        default_priority: editing.default_priority || 3,
        active: editing.active !== false,
      };
      if (editing.id) {
        await axios.patch(`${API_URL}/api/service-request-types/${editing.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API_URL}/api/service-request-types`, payload, { headers: authHeader() });
      }
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete request type "${t.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/service-request-types/${t.id}`, { headers: authHeader() });
      toast.success('Deleted'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="service-request-types-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#0E7490]" /> Service Request Types
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Configure what your service providers can request. Tie a type to a form to capture structured data on submit.</p>
        </div>
        <Button onClick={() => setEditing({ name: '', default_priority: 3, active: true })} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="add-type-btn">
          <Plus className="w-4 h-4 mr-1" /> New type
        </Button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>
      ) : types.length === 0 ? (
        <Card className="p-12 text-center text-sm text-[#94A3B8] border-[#E2E8F0]" data-testid="no-types">
          No request types yet. Add one so providers can submit requests.
        </Card>
      ) : (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0] text-left text-[#64748B] text-xs">
                <tr>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Linked form</th>
                  <th className="px-3 py-2 font-medium">Default priority</th>
                  <th className="px-3 py-2 font-medium">Assignee role</th>
                  <th className="px-3 py-2 font-medium">Active</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map(t => (
                  <tr key={t.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`type-row-${t.id}`}>
                    <td className="px-3 py-2 text-[#0A2342] font-medium">{t.name}<div className="text-[10px] text-[#94A3B8]">{t.description || ''}</div></td>
                    <td className="px-3 py-2 text-[#64748B]">{t.linked_form_name || '—'}</td>
                    <td className="px-3 py-2 text-[#64748B]">P{t.default_priority}</td>
                    <td className="px-3 py-2 text-[#64748B]">{t.assignee_role || '—'}</td>
                    <td className="px-3 py-2">{t.active ? <span className="text-emerald-700">Yes</span> : <span className="text-[#94A3B8]">No</span>}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setEditing(t)} className="text-[#0E7490] hover:bg-[#0E7490]/10 rounded p-1" data-testid={`edit-type-${t.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(t)} className="text-rose-600 hover:bg-rose-50 rounded p-1 ml-1" data-testid={`delete-type-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="type-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'New'} request type</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div>
                <Label>Name *</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="type-name-input" />
              </div>
              <div>
                <Label>Description</Label>
                <textarea rows={2} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Linked form (optional)</Label>
                  <select value={editing.linked_form_id || ''} onChange={(e) => setEditing({ ...editing, linked_form_id: e.target.value || null })} className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-2 bg-white">
                    <option value="">— No form —</option>
                    {forms.map(f => <option key={f.id || f._id} value={f.id || f._id}>{f.name || f.title}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Default priority</Label>
                  <select value={editing.default_priority || 3} onChange={(e) => setEditing({ ...editing, default_priority: parseInt(e.target.value, 10) })} className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-2 bg-white">
                    <option value={1}>P1 — Critical</option>
                    <option value={2}>P2 — High</option>
                    <option value={3}>P3 — Moderate</option>
                    <option value={4}>P4 — Low</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Default assignee role (optional)</Label>
                  <select value={editing.assignee_role || ''} onChange={(e) => setEditing({ ...editing, assignee_role: e.target.value || null })} className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-2 bg-white">
                    <option value="">— None —</option>
                    {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-[#0A2342]">
                    <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                    Active
                  </label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="save-type-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
